import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { setNebraskaScheme } from '@/modules/common/services/theme';
import {
  isPartnerRunningStandalone,
  redirectToPartnerArea
} from '@/modules/common/helpers/partner';
import { redirectToUrl } from '@/modules/common/helpers/route/route';
import {
  trackLogin,
  trackPageView,
  trackSendEmailClick,
  trackSendPasswordClick
} from '@/modules/authentication/helpers/track-signin-events';
import {
  ICustomTheme,
  setCustomTheme,
  StorageKeys
} from '@/modules/common/services/local-storage';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  BrandImage,
  EBrandImageLogos,
  EBrandImageSchemas,
  Heading,
  SigninTemplate,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import FadeSlideTransition from '@/modules/common/components/FadeSlideTransition/FadeSlideTransition.vue';
import { SignInFormStepComponents } from './helpers/step-component';
import { SignInForm, SignInFormSteps } from './types';
import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';
import { NotificationTypes } from '@/modules/wallet-builder/src/types';
import {
  ILoginResponse,
  acceptTerms,
  getThemeByParam,
  login,
  verifyTermsAndMfa
} from '@/modules/common/services/http/account';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { CustomerModule } from '@/modules/common/store/CustomerModule';
import { ICustomer } from '@/types/models';
import {
  IMFAInfo,
  IMFALoginResponse
} from '@/modules/authentication/services/mfa';
import { MFALogin } from '@/modules/authentication/views/SecurityView/components/MFALogin';
import MembershipTermsModal from '@/modules/authentication/components/MembershipTermsModal/MembershipTermsModal.vue';
import { getProspectStatus } from '@/modules/onboarding/signup/services/api';
import { ECoexistenceStatus } from '@/modules/onboarding/signup/types';


export default /*#__PURE__*/_defineComponent({
  __name: 'SignInView',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const mfaInfo = ref<IMFAInfo | null>(null);
const theme = ref<ICustomTheme | null>(null);
const themeLoading = ref<boolean>(false);
const formStep = ref<SignInFormSteps>(SignInFormSteps.Email);
const loadingColor = ref<string | null>(NebraskaColors.elementOverImage);
const recaptchaToken = ref<string>('');
const lastRecaptchaAction = ref<RecaptchaActions>();
const recaptcha = ref<IRecaptcha | null>(null);
const isLoading = ref<boolean>(false);
const isMembershipTermsModalOpen = ref<boolean>(false);
const loginData = ref<ILoginResponse | IMFALoginResponse | null>(null);
const form = ref<SignInForm>({
  email: '',
  password: '',
  token: ''
});

const gradientBackgroundActive = computed(() => {
  return !(theme && theme.value?.type === 'BrandManaged');
});

const slideAnimationActive = computed(() => {
  return window.innerWidth > 1024;
});

async function handleRecaptcha(RecaptchaAction: RecaptchaActions) {
  isLoading.value = true;
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;

  try {
    lastRecaptchaAction.value = RecaptchaAction;
    await recaptchaEl.onChallenge(RecaptchaAction);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
    isLoading.value = false;
  }
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
  form.value.token = recaptchaToken.value;
  if (
    lastRecaptchaAction.value === RecaptchaActions.SIGN_IN &&
    recaptchaToken.value
  ) {
    await submit();
  } else if (
    lastRecaptchaAction.value === RecaptchaActions.PROSPECT_STATUS &&
    recaptchaToken.value
  ) {
    await handleProspectStatus();
  }
}

function resetRecaptcha() {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;
  if (recaptchaEl) recaptchaEl.onReset();
}

function isMfaError(
  err: unknown
): err is { message: string; custom: IMFAInfo } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    'custom' in err &&
    err.message === 'error_login_auth_token_verify'
  );
}

async function submit() {
  try {
    const response = await login(form.value);
    loginData.value = response;
    loginHandler(response);
  } catch (err: unknown) {
    if (isMfaError(err)) {
      mfaInfo.value = err.custom;
    } else {
      resetRecaptcha();
      notify({
        group: 'wrn-notifications',
        text: 'E-mail ou senha inválidos, tente novamente',
        type: NotificationTypes.error
      });
    }
    isLoading.value = false;
  }
}
async function loginHandler(data: IMFALoginResponse | ILoginResponse) {
  loginData.value = data;
  const response = await verifyTermsAndMfa(data.accessToken);
  if (!response.termsAccepted) {
    isMembershipTermsModalOpen.value = true;
    return;
  }

  handleSignIn();
}

async function handleAcceptTerms() {
  loginData.value && (await acceptTerms(loginData.value.accessToken));
  isMembershipTermsModalOpen.value = false;
  await handleSignIn();
}

async function handleSignIn() {
  if (loginData.value) {
    localStorage.setItem(StorageKeys.AccessToken, loginData.value.accessToken);
    setLocalStorageData(loginData.value);
  }
  await FeatureFlagsModule.fetchFeatureFlags();
  trackLogin();
  if (loginData.value && loginData.value.user.clientType.b2bPartner) {
    partnerSignin(loginData.value.user);
  } else {
    clientSignin();
  }
}

async function handleProspectStatus() {
  try {
    const response = await getProspectStatus(
      form.value.email,
      recaptchaToken.value
    );
    if (response.status === ECoexistenceStatus.Prospect) {
      changeToPasswordCreationStep();
    } else {
      changeToPasswordSignInStep();
    }
  } catch (err) {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

function setLocalStorageData(data: ILoginResponse | IMFALoginResponse) {
  const localStorageData = {
    [StorageKeys.Brand]: JSON.stringify(data?.brand || null),
    [StorageKeys.CustomerId]: data.user._id!,
    [StorageKeys.IsBrandOwner]: String(data.isBrandOwner)
  };

  localStorage.setItem('warren.home.experience', 'true');
  Object.entries(localStorageData).forEach(([key, value]) =>
    localStorage.setItem(key, value)
  );
  if (data.user.clientType.whitelabel)
    localStorage.setItem(StorageKeys.WhitelabelTheme, 'whitelabel');
}

async function clientSignin() {
  await CustomerModule.fetchCustomer();
  const redirect = route.query.redirect as string;
  router.replace(redirect || '/home');
  isLoading.value = false;
}

function partnerSignin(user: ICustomer) {
  const localStorageFeatures = localStorage.getItem(StorageKeys.Features);
  if (localStorageFeatures) {
    const featuresArray = JSON.parse(localStorageFeatures);
    localStorage.setItem(
      StorageKeys.PartnerFeatures,
      JSON.stringify(featuresArray)
    );
  }

  localStorage.setItem(StorageKeys.Partner, JSON.stringify(user));
  localStorage.setItem(StorageKeys.IsPartnerAccountSession, 'true');

  if (!isPartnerRunningStandalone()) {
    router.push({ name: 'b2b-dash' });
  } else {
    redirectToPartnerArea(user._id);
  }
  isLoading.value = false;
}

async function handleEmailSubmit(email: string) {
  trackSendEmailClick();
  isLoading.value = true;
  form.value.email = email;
  await handleRecaptcha(RecaptchaActions.PROSPECT_STATUS);
}

async function handlePasswordSubmit(password: string) {
  trackSendPasswordClick();
  isLoading.value = true;
  form.value.password = password;
  await handleRecaptcha(RecaptchaActions.SIGN_IN);
}

const changeToEmailStep = () => {
  form.value.password = '';
  formStep.value = SignInFormSteps.Email;
};

const changeToPasswordSignInStep = () => {
  trackSendEmailClick();
  formStep.value = SignInFormSteps.PasswordSignIn;
};

const changeToPasswordCreationStep = () => {
  trackSendEmailClick();
  formStep.value = SignInFormSteps.PasswordCreation;
};

const componentStep = computed(() => {
  return SignInFormStepComponents[formStep.value];
});

async function setTheme(
  themeParam: string,
  paramType: 'domain' | 'email'
): Promise<void> {
  try {
    loadingColor.value = NebraskaColors.backgroundSecondary;
    themeLoading.value = true;
    const themeResponse = await getThemeByParam(paramType, themeParam);
    theme.value = themeResponse;
    if (!theme.value) return;
    setNebraskaScheme(theme.value);
    setCustomTheme(theme.value);
  } catch (err) {
    console.error(err);
  } finally {
    loadingColor.value = null;
    themeLoading.value = false;
  }
}

async function checkTheme(): Promise<void> {
  const storageTheme = localStorage.getItem(StorageKeys.Theme);
  if (storageTheme) {
    theme.value = JSON.parse(storageTheme);
    loadingColor.value = null;
    return;
  }

  const brand = route.query.brand;
  if (typeof brand === 'string') {
    await setTheme(brand, 'domain');
  } else {
    loadingColor.value = null;
  }
}

async function setFormPassword(password: string) {
  form.value.password = password;
}

onMounted(() => {
  trackPageView();
  checkTheme();
});

provide('isLoading', isLoading);
provide('handlePasswordSubmit', handlePasswordSubmit);
provide('handleEmailSubmit', handleEmailSubmit);
provide('changeToEmailStep', changeToEmailStep);
provide('setTheme', setTheme);
provide('form', form);
provide('setFormPassword', setFormPassword);
provide('handleRecaptchaSignIn', handleRecaptcha);

return { __sfc: true,route, router, mfaInfo, theme, themeLoading, formStep, loadingColor, recaptchaToken, lastRecaptchaAction, recaptcha, isLoading, isMembershipTermsModalOpen, loginData, form, gradientBackgroundActive, slideAnimationActive, handleRecaptcha, onRecaptchaToken, resetRecaptcha, isMfaError, submit, loginHandler, handleAcceptTerms, handleSignIn, handleProspectStatus, setLocalStorageData, clientSignin, partnerSignin, handleEmailSubmit, handlePasswordSubmit, changeToEmailStep, changeToPasswordSignInStep, changeToPasswordCreationStep, componentStep, setTheme, checkTheme, setFormPassword, redirectToUrl, NebraskaColors, BaseText, BrandImage, EBrandImageLogos, EBrandImageSchemas, Heading, SigninTemplate, SkeletonLoader, FadeSlideTransition, Recaptcha, MFALogin, MembershipTermsModal }
}

})